import axios from 'axios';
import API_CONFIG from './apiConfig';
import csrfManager from './utils/csrfMainManager';
import rateLimitService from '../rateLimit/rateLimitService';

// ================== API CLIENTS ==================
export const apiClient = axios.create({
  baseURL: API_CONFIG.API.baseUrl,
  timeout: API_CONFIG.API.timeout,
  headers: { Accept: 'application/json' },
  withCredentials: API_CONFIG.API.withCredentials,
  xsrfCookieName: 'csrf_token',
  xsrfHeaderName: 'X-CSRF-Token',
});

export const formEncodedApiClient = axios.create({
  baseURL: API_CONFIG.API.baseUrl,
  timeout: API_CONFIG.API.timeout,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded',
  },
  withCredentials: API_CONFIG.API.withCredentials,
  xsrfCookieName: 'csrf_token',
  xsrfHeaderName: 'X-CSRF-Token',
});

// ================== REQUEST INTERCEPTOR ==================
const requestInterceptor = async (config) => {
  try {
    const csrfToken = csrfManager.getTokenFromCookie();

    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    } else {
      console.warn('[CSRF] No CSRF token present for request:', config.url);
    }

  } catch (err) {
    console.warn('[Axios] Request interceptor error:', err);
  }

  return config;
};


// Add error handling for request interceptor
const requestErrorHandler = (error) => {
  console.warn('[Axios] Request interceptor error:', error);
  return Promise.reject(error);
};

apiClient.interceptors.request.use(requestInterceptor, requestErrorHandler);
formEncodedApiClient.interceptors.request.use(requestInterceptor, requestErrorHandler);

// ================== RESPONSE INTERCEPTOR ==================
const setupResponseInterceptor = (client) => {
  client.interceptors.response.use(
    (response) => {
      try {
        // Update rate limit info if service exists
        if (rateLimitService && rateLimitService.updateLimits) {
          rateLimitService.updateLimits(response.headers);
        }
      } catch (err) {
        console.warn('[RateLimit] Failed to update limits:', err);
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Handle network/timeout errors
      if (error.code === 'ECONNABORTED') {
        console.warn('[Network] Request timeout:', error.message);
        return Promise.reject(new Error('Request timed out. Please try again.'));
      }

      if (error.message === 'Network Error') {
        console.warn('[Network] Network error:', error.message);
        return Promise.reject(new Error('Network error. Please check your connection.'));
      }

      // Handle CSRF 403 errors with retry
      if (
        error.response?.status === 403 &&
        error.response?.data?.detail?.includes('CSRF') &&
        !originalRequest._csrfRetry
      ) {
        originalRequest._csrfRetry = true;
        try {
          //console.log('[CSRF] Refreshing token and retrying request');
          const newToken = await csrfManager.refresh();
          if (newToken && originalRequest.method !== 'get') {
            originalRequest.headers['X-CSRF-Token'] = newToken;
            return client(originalRequest);
          }
        } catch (csrfError) {
          console.warn('[CSRF] Retry after refresh failed:', csrfError);
        }
      }

      // Log error details for debugging
      const status = error.response?.status;
      const detail = error.response?.data?.detail || error.message || 'Request failed';

      console.warn(`[Axios] Request failed (${status}):`, {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status,
        detail,
        error: error.response?.data
      });

      // error_handling.py's handle_validation_error always sets
      // error.message to the constant string "Validation error" -- the
      // real per-field reason lives in error.details[] (Pydantic's own
      // [{loc, msg, type}] shape), which no thunk in this app ever reads.
      // Every 422 in the entire app showed the generic category label
      // instead of the actual field/reason. Rewriting error.message here,
      // in the one shared interceptor nearly every API call already goes
      // through, fixes every write path at once instead of touching each
      // thunk's error-reading code individually.
      const validationDetails = error.response?.data?.error?.details;
      if (Array.isArray(validationDetails) && validationDetails.length > 0) {
        const readableMessage = validationDetails
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : d.loc;
            return field && field !== 'body' ? `${field}: ${d.msg}` : d.msg;
          })
          .filter(Boolean)
          .join('; ');

        if (readableMessage) {
          error.message = readableMessage;
          if (error.response?.data?.error) {
            error.response.data.error.message = readableMessage;
          }
        }
      }

      return Promise.reject(error);
    }
  );
};

setupResponseInterceptor(apiClient);
setupResponseInterceptor(formEncodedApiClient);

export default apiClient;